import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/rich-text/RichTextEditor";
import { quoteOrderItemsApi, toDisplayHtml, toStorageHtml } from "@/lib/quote-order-items";
import { quoteFilesApi } from "@/lib/quote-files";
import { ApiError } from "@/lib/api";

export interface QuoteOrderItemsCardProps {
  quoteId: number;
  editable: boolean;
}

/**
 * Backlog #9: large free-text "Order items" body below the structured items grid.
 * Stored as sanitised HTML; rendered in the quote PDF above the items table.
 */
export function QuoteOrderItemsCard({ quoteId, editable }: QuoteOrderItemsCardProps) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["quote-order-items", quoteId],
    queryFn: () => quoteOrderItemsApi.get(quoteId),
  });

  const [html, setHtml] = useState("");
  // Last loaded/saved display html — Save is gated on html differing from this,
  // so a no-op click can't re-PUT (and emit a spurious audit event). Kept in
  // sync by the load effect (which the save-success invalidation re-triggers).
  const [savedHtml, setSavedHtml] = useState("");

  // Object URLs minted for inline images (both by toDisplayHtml when loading
  // the body, and by uploadImage when the user inserts a new image) must be
  // revoked or they leak for the life of the tab. We track every URL this
  // component currently owns in `createdUrls` and revoke them all on unmount;
  // when a fresh display html replaces the previous one we revoke the old batch
  // first. toDisplayHtml reports each URL it mints via a callback so we can
  // revoke them even when the fetch is abandoned (see the cancelled branch).
  const createdUrls = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (query.data) {
      // Collect this call's URLs as they're minted (synchronously, before the
      // promise resolves) so none can slip through the cancelled branch.
      const mintedThisRun: string[] = [];
      void toDisplayHtml(query.data.bodyHtml ?? "", (url) => mintedThisRun.push(url)).then(
        (display) => {
          if (cancelled) {
            // Unmounted / superseded mid-fetch: nobody will ever render these
            // URLs, so revoke them here — they never entered createdUrls.
            mintedThisRun.forEach((url) => URL.revokeObjectURL(url));
            return;
          }
          // Revoke the previous batch before adopting the new one.
          createdUrls.current.forEach((url) => URL.revokeObjectURL(url));
          createdUrls.current = mintedThisRun;
          setHtml(display);
          setSavedHtml(display);
        },
      );
    }
    return () => { cancelled = true; };
  }, [query.data]);

  // Revoke any remaining object URLs when the card unmounts.
  useEffect(() => {
    return () => { createdUrls.current.forEach((url) => URL.revokeObjectURL(url)); };
  }, []);

  const save = useMutation({
    mutationFn: () =>
      quoteOrderItemsApi.put(quoteId, {
        bodyHtml: toStorageHtml(html),
        rowVersionBase64: query.data?.rowVersionBase64 ?? "",
      }),
    onSuccess: () => {
      toast.success("Order items saved.");
      void qc.invalidateQueries({ queryKey: ["quote-order-items", quoteId] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("Order items changed since you opened them — reloading the latest.");
        void qc.invalidateQueries({ queryKey: ["quote-order-items", quoteId] });
      } else {
        toast.error("Couldn't save order items.");
      }
    },
  });

  const isDirty = html !== savedHtml;

  const uploadImage = async (file: File) => {
    const up = await quoteFilesApi.upload(quoteId, file);
    const stable = quoteFilesApi.downloadUrl(quoteId, up.file.quoteFileId);
    const objectUrl = URL.createObjectURL(file);
    createdUrls.current.push(objectUrl);
    return { url: objectUrl, attrs: { "data-qf-src": stable } };
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Order items
        </h3>

        {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!query.isLoading && editable && (
          <>
            <RichTextEditor
              value={html}
              onChange={(v) => setHtml(v)}
              onUploadImage={uploadImage}
              disabled={save.isPending}
              // Restrict to tags the server sanitiser + PDF composer support —
              // otherwise headings/quotes/code/rules render but vanish on save.
              starterKitOptions={{
                heading: false,
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
              }}
            />
            <Button size="sm" disabled={!isDirty || save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        )}

        {!query.isLoading && !editable && (
          html
            // Safe: bodyHtml is sanitised server-side on write (Task 4); this
            // read-only view only ever renders that already-sanitised HTML.
            ? <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
            : <p className="text-sm text-muted-foreground">No order items text.</p>
        )}
      </CardContent>
    </Card>
  );
}
